import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import FilterMenu from "../../../components/FilterMenu";
import { DynamicDatePicker } from "../../../components/DatePicker";
import {
  getJvvnlCircleForecast,
  getJvvnlEntityMetrics,
  getJvvnlFeederForecast,
  getJvvnlFeederMetrics,
  getJvvnlGeneration,
  getJvvnlPlants,
  JvvnlEntityMetricsResponse,
} from "../../../Rest_api/RestApi";
import { formatDateForAPI } from "../../Rajasthan/Utility/const";
import { JvvnlFeederDemand } from "../Demand/FeederDemand";
import type { JvvnlPlant } from "../../../models/jvvnl";

type EmbeddedNet = {
  level: "transco" | "discom_sub" | "plant" | "circle" | "feeder";
  id: number | string;
  label: string;
};

const VARIABLE_ITEMS = ["Demand", "Generation", "Net Load"];

const WEATHER_ITEMS = [
  { value: "apparent_temperature", label: "Apparent Temp (°C)" },
  { value: "cloud_cover", label: "Cloud Cover (%)" },
  { value: "direct_radiation_instant", label: "Direct Radiation Instant (W/m²)" },
  { value: "relative_humidity_2m", label: "Humidity (%)" },
  { value: "precipitation", label: "Precipitation (mm)" },
  { value: "rain", label: "Rain (mm)" },
  { value: "sunshine_duration", label: "Sunshine Duration (s)" },
  { value: "temperature_2m", label: "Temperature (°C)" },
  { value: "wind_speed_10m", label: "Wind 10m (km/h)" },
];

function MetricBlock({
  title,
  accentColor,
  peakValue,
  averageValue,
  unit,
}: {
  title: string;
  accentColor: string;
  peakValue: number | null;
  averageValue: number | null;
  unit: string;
}) {
  const fmt = (v: number | null) => {
    if (v == null) return "—";
    return v.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  return (
    <div className="relative bg-white rounded-xl border border-[#DFEAF2] px-5 py-3 flex-1 min-w-0">
      <div
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full"
        style={{ backgroundColor: accentColor }}
      />
      <p className="text-[13px] font-medium text-[#525062] mb-3">{title}</p>
      <div className="flex items-stretch">
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-medium text-[#6B7280] uppercase tracking-wide mb-0.5">
            Peak
          </p>
          <p className="text-[20px] font-medium text-[#14171F] leading-tight">
            {fmt(peakValue)}{" "}
            <span className="text-[11px] font-normal text-[#6B7280]">
              {unit}
            </span>
          </p>
        </div>
        <div className="w-px bg-[#DFEAF2] mx-4 self-stretch" />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-medium text-[#6B7280] uppercase tracking-wide mb-0.5">
            Average
          </p>
          <p className="text-[20px] font-medium text-[#14171F] leading-tight">
            {fmt(averageValue)}{" "}
            <span className="text-[11px] font-normal text-[#6B7280]">
              {unit}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export function NetConsumptionPopup({
  embedded,
  onClose,
  initialTab,
  forecastDateOverride,
}: {
  embedded: EmbeddedNet;
  onClose?: () => void;
  initialTab?: "Actual" | "Forecast";
  forecastDateOverride?: string | null;
}) {
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const reduxRange =
    DefaultDates?.Metric?.defaultRangeDate ??
    DefaultDates?.Overview?.defaultRangeDate ??
    DefaultDates?.Actual?.defaultRangeDate;

  const [localRange, setLocalRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  } | null>(() => {
    if (initialTab === "Forecast" && forecastDateOverride) {
      const d = new Date(forecastDateOverride);
      return { startDate: d, endDate: d, key: "selection" };
    }
    if (!reduxRange?.startDate || !reduxRange?.endDate) return null;
    if (initialTab === "Actual") {
      const end = new Date(reduxRange.endDate);
      return { startDate: end, endDate: end, key: "selection" };
    }
    // Always initialize localRange so the date picker is always visible
    return {
      startDate: new Date(reduxRange.startDate),
      endDate: new Date(reduxRange.endDate),
      key: "selection",
    };
  });

  useEffect(() => {
    if (initialTab === "Forecast" && forecastDateOverride) {
      const d = new Date(forecastDateOverride);
      setLocalRange((prev) => {
        const fmtPrev = prev?.startDate?.toISOString().slice(0, 10);
        if (fmtPrev === forecastDateOverride) return prev;
        return { startDate: d, endDate: d, key: "selection" };
      });
    }
  }, [initialTab, forecastDateOverride]);

  const range = localRange ?? reduxRange;

  const dateParams = useMemo(() => {
    if (!range?.startDate || !range?.endDate) return null;
    return {
      startDate: formatDateForAPI(range.startDate),
      endDate: formatDateForAPI(range.endDate),
    };
  }, [range?.startDate, range?.endDate]);

  const [selectedVariables, setSelectedVariables] = useState<string[]>(() => {
    if (embedded.level === "plant") return ["Generation"];
    if (embedded.level === "feeder")
      return initialTab === "Forecast" ? ["Forecast"] : ["Forecast"];
    return ["Demand", "Generation", "Net Load"];
  });
  const [plantMetrics, setPlantMetrics] = useState<{
    peakGenKw: number | null;
    avgGenKw: number | null;
    peakTemp: number | null;
    avgTemp: number | null;
  } | null>(null);
  const [selectedWeather, setSelectedWeather] = useState<string>("");
  const [metrics, setMetrics] = useState<JvvnlEntityMetricsResponse | null>(
    null,
  );
  const [feederForecastMetrics, setFeederForecastMetrics] =
    useState<JvvnlEntityMetricsResponse | null>(null);
  const [circleForecastMetrics, setCircleForecastMetrics] =
    useState<JvvnlEntityMetricsResponse | null>(null);

  useEffect(() => {
    if (embedded.level === "plant") {
      setSelectedVariables(["Generation"]);
      return;
    }
    if (embedded.level === "feeder") {
      setSelectedVariables(["Forecast"]);
      return;
    }
    setSelectedVariables(["Demand", "Generation", "Net Load"]);
  }, [embedded.level, initialTab]);

  useEffect(() => {
    if (!dateParams) return;
    setMetrics(null);
    setFeederForecastMetrics(null);
    setCircleForecastMetrics(null);
    if (embedded.level === "feeder") {
      setSelectedVariables((prev) => prev.filter((v) => v !== "Actual"));
    }
    if (embedded.level === "plant") {
      return;
    }
    let cancelled = false;
    const level: "transco" | "discom_sub" | "circle" | "feeder" =
      embedded.level;
    if (level === "feeder") {
      const toMetrics = (full: any): JvvnlEntityMetricsResponse | null => {
        if (full?.peak_mw == null) return null;
        return {
          // At feeder level the mapper stores raw kW in peak_mw/avg_mw
          // (API emits *_kw at this level — no scaling needed).
          peak_load_kw: Number(full.peak_mw),
          avg_load_kw: full.avg_mw != null ? Number(full.avg_mw) : undefined,
          peak_temp_c: full.peak_weather?.temp
            ? Number(full.peak_weather.temp)
            : undefined,
          avg_temp_c: full.avg_weather?.temp
            ? Number(full.avg_weather.temp)
            : undefined,
        };
      };
      Promise.all([
        getJvvnlFeederMetrics({
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
          level: "feeder",
          ids: [embedded.id],
        }),
        getJvvnlFeederForecast({
          ids: [Number(embedded.id)],
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
        }),
      ])
        .then(([aRes, fRes]) => {
          if (cancelled) return;
          const actualMetrics = toMetrics(aRes.results?.[0]?.metrics?.full);
          setMetrics(actualMetrics);
          setFeederForecastMetrics(toMetrics(fRes.results?.[0]?.metrics?.full));
          setSelectedVariables((prev) => {
            const withoutActual = prev.filter((v) => v !== "Actual");
            return actualMetrics != null
              ? ["Actual", ...withoutActual]
              : withoutActual;
          });
        })
        .catch(() => {
          if (!cancelled) {
            setMetrics(null);
            setFeederForecastMetrics(null);
            setSelectedVariables((prev) => prev.filter((v) => v !== "Actual"));
          }
        });
    } else if (level === "circle") {
      const circleName = String(embedded.id);
      getJvvnlEntityMetrics({
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        level: "circle",
        name: circleName,
      })
        .then((res) => {
          if (cancelled) return;
          const hasActual = res?.peak_load_kw != null;
          setMetrics(hasActual ? res : null);
          if (!hasActual) {
            return getJvvnlCircleForecast({
              startDate: dateParams.startDate,
              endDate: dateParams.endDate,
            }).then((fRes) => {
              if (cancelled) return;
              const match = fRes.results.find((r: any) => r.name === circleName) ?? fRes.results[0];
              const full = match?.metrics?.full;
              setCircleForecastMetrics(
                full?.peak_mw != null
                  ? {
                      peak_load_kw: Number(full.peak_mw) * 1000,
                      avg_load_kw: full.avg_mw != null ? Number(full.avg_mw) * 1000 : undefined,
                      peak_temp_c: full.peak_weather?.temp != null ? Number(full.peak_weather.temp) : undefined,
                      avg_temp_c: full.avg_weather?.temp != null ? Number(full.avg_weather.temp) : undefined,
                    }
                  : null,
              );
            });
          } else {
            setCircleForecastMetrics(null);
          }
        })
        .catch(() => {
          if (!cancelled) { setMetrics(null); setCircleForecastMetrics(null); }
        });
    } else {
      getJvvnlEntityMetrics({
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        level,
        id: Number(embedded.id),
      })
        .then((res) => {
          if (!cancelled) setMetrics(res);
        })
        .catch(() => {
          if (!cancelled) setMetrics(null);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [dateParams, embedded.id, embedded.level]);

  useEffect(() => {
    if (!dateParams) return;
    if (embedded.level !== "plant") {
      setPlantMetrics(null);
      return;
    }

    let cancelled = false;
    const plantId = Number(embedded.id);
    if (!Number.isFinite(plantId)) {
      setPlantMetrics(null);
      return;
    }

    (async () => {
      try {
        const plants = ((await getJvvnlPlants()) || []) as JvvnlPlant[];
        const plant = plants.find((p) => (p.id ?? p.plant_id) === plantId);
        if (!plant) {
          if (!cancelled) setPlantMetrics(null);
          return;
        }

        const plantLocation = new Map<number, number>();
        for (const p of plants) {
          const pid = p.id ?? p.plant_id;
          const loc = p.location ?? p.location_id;
          if (pid != null && loc != null) plantLocation.set(pid, loc);
        }

        const { rows, weatherByLocation } = await getJvvnlGeneration({
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
          plantIds: [plantId],
          plantLocation,
        });

        if (cancelled) return;

        const genValues = rows
          .filter((r) => r.plant_id === plantId)
          .map((r) => r.generation_kw)
          .filter((v): v is number => typeof v === "number" && isFinite(v));
        const peakGenKw = genValues.length ? Math.max(...genValues) : null;
        const avgGenKw = genValues.length
          ? genValues.reduce((sum, v) => sum + v, 0) / genValues.length
          : null;

        const locId = plant.location ?? plant.location_id;
        const weatherRows =
          locId != null ? (weatherByLocation.get(locId) ?? []) : [];
        const tempValues = weatherRows
          .map((w) => w.temperature_2m)
          .filter((v): v is number => typeof v === "number" && isFinite(v));
        const peakTemp = tempValues.length ? Math.max(...tempValues) : null;
        const avgTemp = tempValues.length
          ? tempValues.reduce((sum, v) => sum + v, 0) / tempValues.length
          : null;

        setPlantMetrics({ peakGenKw, avgGenKw, peakTemp, avgTemp });
      } catch {
        if (!cancelled) setPlantMetrics(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dateParams, embedded.id, embedded.level]);

  // feeder + discom_sub + plant: keep kW; transco/circle: convert kW→MW for display.
  const showAsKw =
    embedded.level === "feeder" ||
    embedded.level === "discom_sub" ||
    embedded.level === "plant";
  const loadUnit = showAsKw ? "kW" : "MW";
  const activeMetrics = metrics ?? (embedded.level === "circle" ? circleForecastMetrics : null);
  const peakLoadDisplay =
    embedded.level === "plant"
      ? (plantMetrics?.peakGenKw ?? null)
      : activeMetrics?.peak_load_kw != null
        ? showAsKw
          ? activeMetrics.peak_load_kw
          : activeMetrics.peak_load_kw / 1000
        : null;
  const avgLoadDisplay =
    embedded.level === "plant"
      ? (plantMetrics?.avgGenKw ?? null)
      : activeMetrics?.avg_load_kw != null
        ? showAsKw
          ? activeMetrics.avg_load_kw
          : activeMetrics.avg_load_kw / 1000
        : null;
  const peakTemp =
    embedded.level === "plant"
      ? (plantMetrics?.peakTemp ?? null)
      : embedded.level === "feeder"
        ? (metrics?.peak_temp_c ?? feederForecastMetrics?.peak_temp_c ?? null)
        : (activeMetrics?.peak_temp_c ?? null);
  const avgTemp =
    embedded.level === "plant"
      ? (plantMetrics?.avgTemp ?? null)
      : embedded.level === "feeder"
        ? (metrics?.avg_temp_c ?? feederForecastMetrics?.avg_temp_c ?? null)
        : (activeMetrics?.avg_temp_c ?? null);

  return (
    <div className="bg-[#F4F6F9] rounded-2xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-white rounded-t-2xl border-b border-[#E5EAF0]">
        <span className="text-[18px] font-medium text-[#525062]">
          {embedded.label}
          <span className="text-[#8C8A96] font-normal ml-1">
            (
            {embedded.level === "transco"
              ? "Transco"
              : embedded.level === "discom_sub"
                ? "Discom Substation"
                : embedded.level === "circle"
                  ? "Circle"
                  : embedded.level === "feeder"
                    ? "Feeder"
                    : "Plant"}
            )
          </span>
          {initialTab === "Forecast" && (
            <span className="text-[14px] font-normal text-[#8C8A96] ml-2">
              - Forecasted
            </span>
          )}
        </span>
        <div className="flex items-center gap-3">
          {localRange && (
            <DynamicDatePicker
              setDate={(dates) => {
                if (dates[0]) setLocalRange(dates[0]);
              }}
              defaultDates={localRange}
            />
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="text-[#6B7280] text-lg leading-none px-2"
              aria-label="Close"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Body: sidebar + content */}
      <div className="flex gap-4 p-4">
        {/* Sidebar */}
        <div className="w-[180px] shrink-0 bg-white rounded-xl border border-[#E5EAF0] p-3">
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Variable"
              items={
                embedded.level === "plant"
                  ? ["Generation"]
                  : embedded.level === "feeder"
                    ? ["Actual", "Forecast", "Weather"]
                    : VARIABLE_ITEMS
              }
              selectedItems={selectedVariables}
              setSelectedItems={(next) => setSelectedVariables(next)}
              sortAlphabetically={false}
            />
            <FilterMenu
              title="Weather"
              items={WEATHER_ITEMS}
              showEmptySelected={false}
              selectedItems={selectedWeather ? [selectedWeather] : []}
              onSelect={(value) => {
                const newWeather = selectedWeather === value ? "" : value;
                setSelectedWeather(newWeather);
                if (embedded.level === "feeder") {
                  setSelectedVariables((prev) => {
                    const base = prev.filter((v) => v !== "Weather");
                    return newWeather ? [...base, "Weather"] : base;
                  });
                }
              }}
              sortAlphabetically={false}
            />
          </div>
        </div>

        {/* Right column */}
        <div className="flex-1 min-w-0 flex flex-col gap-3 !h-[calc(100vh-200px)]">
          {/* Metric cards */}
          <div className="flex gap-3">
            {embedded.level === "feeder" ? (
              metrics != null ? (
                <MetricBlock
                  title="Actual Load"
                  accentColor="#E25A1F"
                  peakValue={metrics.peak_load_kw ?? null}
                  averageValue={metrics.avg_load_kw ?? null}
                  unit="kW"
                />
              ) : feederForecastMetrics != null ? (
                <MetricBlock
                  title="Forecasted Load"
                  accentColor="#16A34A"
                  peakValue={feederForecastMetrics.peak_load_kw ?? null}
                  averageValue={feederForecastMetrics.avg_load_kw ?? null}
                  unit="kW"
                />
              ) : (
                <MetricBlock
                  title="Load"
                  accentColor="#9CA3AF"
                  peakValue={null}
                  averageValue={null}
                  unit="kW"
                />
              )
            ) : (
              <MetricBlock
                title={
                  embedded.level === "plant"
                    ? "Generation"
                    : embedded.level === "circle" && metrics == null && circleForecastMetrics != null
                      ? "Forecasted Load"
                      : "Load"
                }
                accentColor={
                  embedded.level === "circle" && metrics == null && circleForecastMetrics != null
                    ? "#16A34A"
                    : embedded.level === "circle" && metrics == null && circleForecastMetrics == null
                      ? "#9CA3AF"
                      : "#E25A1F"
                }
                peakValue={peakLoadDisplay}
                averageValue={avgLoadDisplay}
                unit={loadUnit}
              />
            )}
            <MetricBlock
              title="Temperature"
              accentColor="#5A4FCF"
              peakValue={peakTemp}
              averageValue={avgTemp}
              unit="°C"
            />
          </div>

          {/* Chart: reuse existing embedded flow */}
          <div className="bg-white rounded-xl border border-[#E5EAF0] [&_.chart-container]:!h-[calc(100vh-450px)] [&_.chartTable]:!h-[calc(100vh-400px)] [&_.gas-chart-container]:!min-h-0 [&_.gas-chart-container]:!flex-1 [&_.gas-chart-container>div]:!min-h-0 [&_.gas-chart-container>div]:!h-full flex-1 flex flex-col [&>div]:flex-1 [&>div]:flex [&>div]:flex-col [&>div]:min-h-0">
            <JvvnlFeederDemand
              embedded={embedded}
              weatherParam={selectedWeather}
              netVariable={
                embedded.level === "feeder"
                  ? null
                  : initialTab !== "Forecast" && selectedVariables.length === 1
                    ? (selectedVariables[0] as
                        | "Demand"
                        | "Generation"
                        | "Net Load")
                    : null
              }
              variablesOverride={
                embedded.level === "feeder"
                  ? selectedWeather
                    ? [...selectedVariables.filter((v) => v !== "Weather"), "Weather"]
                    : selectedVariables.filter((v) => v !== "Weather")
                  : initialTab === "Forecast"
                    ? selectedWeather
                      ? ["Forecast", "Weather"]
                      : ["Forecast"]
                    : selectedWeather
                      ? ["Actual", "Weather"]
                      : ["Actual"]
              }
              forecastVariants={
                embedded.level !== "feeder" && initialTab === "Forecast"
                  ? selectedVariables
                  : null
              }
              dateRangeOverride={localRange}
              forecastDateOverride={
                initialTab === "Forecast" ? forecastDateOverride : null
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
